import React, { useState, Fragment, useContext, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { Cell } from 'react-foundation';
import classnames from 'classnames';
import * as Yup from 'yup';

import PrimaryButton from '../../components/common/buttons/PrimaryButton';
import Loading from '../../components/common/Loading';
import FieldInput from '../../components/common/fields/FieldInput/FieldInput';
import MessageModal from '../../components/MessageModal';
import DoctorLeftNav from '../../components/nav/DoctorLeftNav';

import AuthContext from '../../context/AuthContext';

import messages from './messages';
import useIntl from '../../hooks/useIntl';

import { addToDataLayer } from '../../utils/common';

import { invitePatient } from '../../api/patient';
import styles from './InvitePatientPage.module.scss';

const tabs = ['EMAIL', 'SMS'];

const InivitePatientPage = () => {
  const { formatMessage } = useIntl();

  const { authUser } = useContext(AuthContext);

  const [error, setError] = useState('');
  const [dataLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [displayMessage, setDisplayMessage] = useState({
    title: '',
    message: '',
  });

  const EmailSchema = Yup.object().shape({
    firstName: Yup.string().required('Field Required'),
    email: Yup.string().email().required('Field Required'),
  });
  const SmsSchema = Yup.object().shape({
    firstName: Yup.string().required('Field Required'),
    phone: Yup.string()
      .min(10, 'Please enter valid phone number')
      .max(10, 'Please enter valid phone number')
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        'Please enter valid phone number',
      )
      .required('Phone number is required.'),
  });

  useEffect(() => {
    if (authUser.id) {
      const eventData = {
        event: 'ga4_event',
        properties: {
          event_name: 'invite_patient',
          step_name: 'invite_patient_load',
          clinician_id: authUser.id || '',
          clinician_name: authUser.first_name || '',
          user_type: authUser.profile_type,
        },
      };
      addToDataLayer(eventData);
    }
  }, [authUser]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const { email, firstName, phone } = values;
      const { id } = authUser;

      const eventData = {
        event: 'ga4_event',
        properties: {
          event_name: 'invite_patient',
          step_name: 'invite_patient_submit',
          clinician_id: id || '',
          clinician_name: authUser.first_name || '',
          user_type: authUser.profile_type,
        },
      };
      addToDataLayer(eventData);

      await invitePatient({
        email: email === '' ? null : email,
        firstName,
        doctor_id: id,
        invited_status: 'pending',
        clinic_id: authUser.clinic_id,
        phone: phone === '' ? null : phone,
      });

      setLoading(false);
      setDisplayMessage({
        type: 'success',
        title: formatMessage(messages.modalSuccessText),
        text: '',
      });

      setShowMessageModal(true);
    } catch (error) {
      let errorMsg = '';
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMsg = error.response.data.message;
      }
      setError(errorMsg);
      setLoading(false);
      setDisplayMessage({
        type: 'error',
        title: '',
        text: errorMsg || formatMessage(messages.modalFailureText),
      });
      setShowMessageModal(true);
      throw error;
    }
  };

  const onclickTab = (tab) => {
    setActiveTab(tab);
  };

  return (
    <Fragment>
      <div className={styles.container}>
        <div medium={3} className={classnames(styles.leftnav, 'desktop-only')}>
          <DoctorLeftNav />
        </div>
        {dataLoading ? (
          <Loading />
        ) : (
          <Fragment>
            <div className={styles.lineArt}>
              <img src={require(`../../img/LineArt.svg`)} alt="line-art" />
            </div>
            <div className={styles.contentDiv}>
              <div
                medium={3}
                className={classnames(styles.toptnav, 'mobile-only')}
              ></div>

              <Formik
                initialValues={{ email: '', firstName: '', phone: '' }}
                validationSchema={
                  activeTab === 'EMAIL' ? EmailSchema : SmsSchema
                }
                onSubmit={async (values) => {
                  handleSubmit(values);
                }}
              >
                {({
                  errors,
                  handleChange,
                  values,
                  touched,
                  handleBlur,
                  setFieldValue,
                  isValid,
                }) => {
                  return (
                    <Form className={styles['content']}>
                      <div className={classnames(styles[`form-div`])}>
                        <h2 data-testid="invite-patient header">
                          Invite Patient
                        </h2>
                        <div
                          className={styles['sub-text']}
                          data-testid="invite-patient subtext"
                        >
                          Invite Patient To Join{' '}
                          {authUser?.clinicDetails?.clinic_name ||
                            'Eve Health Systems'}{' '}
                          Platform
                        </div>
                        <nav className={styles.tabs}>
                          <div>
                            <p data-testid="patient send_invitation">
                              How do you want to send your invitation?
                            </p>
                          </div>
                          <div>
                            {tabs.map((tab) => (
                              <button
                                key={tab}
                                className={[
                                  styles.tab,
                                  activeTab === tab ? styles.activeTab : '',
                                ].join(' ')}
                                type="button"
                                onClick={() => {
                                  setFieldValue('email', '');
                                  setFieldValue('phone', '');
                                  onclickTab(tab);
                                }}
                              >
                                <span data-testid={tab}>{tab}</span>
                              </button>
                            ))}
                          </div>
                        </nav>

                        <div className={styles.formInput}>
                          <Cell small={12} medium={12}>
                            <FieldInput
                              label="First Name"
                              className={styles.input}
                              name="firstName"
                              errorText={errors.firstName}
                              onChange={(e) => {
                                setFieldValue('firstName', e.target.value);
                              }}
                              onBlur={handleBlur}
                              value={values.firstName}
                              placeholder="First Name"
                              data-testid="patient name"
                              label-testid="patient name label"
                              error-testid="patient name error"
                            />
                          </Cell>
                          <Cell small={12} medium={12}>
                            {activeTab === 'EMAIL' ? (
                              <FieldInput
                                label="email"
                                className={styles.input}
                                name="email"
                                errorText={errors.email}
                                onChange={(e) => {
                                  setError('');
                                  setFieldValue('email', e.target.value);
                                }}
                                onBlur={handleBlur}
                                value={values.email}
                                placeholder="Patient's Email Address"
                                data-testid="patient email"
                                label-testid="patient email label"
                                error-testid="patient email error"
                              />
                            ) : (
                              <>
                                <FieldInput
                                  label="Mobile number"
                                  className={styles.input}
                                  name="phone"
                                  errorText={errors.phone}
                                  onChange={(e) => {
                                    setError('');
                                    setFieldValue('phone', e.target.value);
                                  }}
                                  onBlur={handleBlur}
                                  value={values.phone}
                                  placeholder="Mobile number"
                                  data-testid="patient phone"
                                  label-testid="patient phone label"
                                  error-testid="patient phone error"
                                />
                              </>
                            )}
                          </Cell>
                        </div>
                        {error && (
                          <div
                            className={styles.error}
                            data-testid="invite_patient error"
                          >
                            {error}
                          </div>
                        )}
                        <div className={classnames(styles[`button-row`])}>
                          <PrimaryButton
                            label="Invite"
                            className={classnames(styles[`button`])}
                            disabled={!isValid}
                            data-testid="invite button"
                          />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Fragment>
        )}
      </div>

      {showMessageModal && (
        <MessageModal
          message={displayMessage}
          onCloseModal={() => setShowMessageModal(false)}
          redirectUrl={'/doctor/my-account'}
        />
      )}
    </Fragment>
  );
};

export default InivitePatientPage;
